<template>
  <v-menu offset-y left>
    <template v-slot:activator="{ props }">
      <div class="notification-activator">
        <v-btn icon variant="text" v-bind="props" class="notification-bell">
          <v-badge
            :content="unreadCount"
            :model-value="unreadCount > 0"
            color="error"
            offset-x="5"
            offset-y="5"
          >
            <v-icon class="notification-icon" :class="{ 'has-unread': unreadCount > 0 }">mdi-bell</v-icon>
          </v-badge>
        </v-btn>
        <v-chip
          v-if="offline"
          class="offline-pill"
          color="warning"
          size="x-small"
          label
          variant="flat"
        >
          Offline
        </v-chip>
      </div>
    </template>

    <v-card min-width="400" max-width="500" class="notification-dropdown">
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Notifications</span>
        <div class="d-flex align-center gap-2">
          <v-btn
            icon="mdi-refresh"
            size="small"
            variant="text"
            @click.stop="refreshNotifications"
            :loading="refreshing"
            :disabled="refreshing"
          ></v-btn>
          <v-chip v-if="unreadCount > 0" size="small" color="primary">
            {{ unreadCount }} new
          </v-chip>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <v-list v-if="notifications.length > 0" max-height="400" class="notification-list">
        <v-list-item
          v-for="notification in recentNotifications"
          :key="notification.id"
          :class="{ 'unread': !notification.is_read }"
          @click="handleNotificationClick(notification)"
          class="notification-item"
        >
          <template v-slot:prepend>
            <v-avatar :color="getNotificationColor(notification.type)" size="small">
              <v-icon :icon="getNotificationIcon(notification.type)" color="white"></v-icon>
            </v-avatar>
          </template>

          <v-list-item-title class="notification-title">
            {{ notification.title }}
          </v-list-item-title>
          <v-list-item-subtitle class="notification-message">
            {{ notification.message }}
          </v-list-item-subtitle>
          <v-list-item-subtitle class="notification-time">
            {{ formatTime(notification.created_at) }}
          </v-list-item-subtitle>
        </v-list-item>
      </v-list>

      <div v-else class="empty-notifications">
        <v-icon size="64" class="empty-icon">mdi-bell-outline</v-icon>
        <p class="empty-message">No notifications</p>
      </div>

      <v-divider v-if="notifications.length > 0"></v-divider>

      <v-card-actions v-if="notifications.length > 0" class="justify-space-between">
        <v-btn variant="text" size="small" @click.stop="viewAllNotifications">
          View All
        </v-btn>
        <div class="d-flex align-center gap-2">
          <v-btn
            variant="text"
            size="small"
            @click.stop="showRead = !showRead"
            :color="showRead ? 'primary' : 'default'"
          >
            {{ showRead ? 'Hide Read' : 'Show Read' }}
          </v-btn>
          <v-btn
            v-if="unreadCount > 0"
            variant="text"
            size="small"
            color="primary"
            @click.stop="markAllAsRead"
          >
            Mark All Read
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { NotificationService } from '@/services/NotificationService.js';

export default {
  name: 'NotificationBell',
  data() {
    return {
      previousUnreadCount: 0,
      refreshing: false,
      showRead: false,
      offline: false
    };
  },
  computed: {
    ...mapGetters('notificationModule', [
      'getAllNotifications',
      'getUnreadCount'
    ]),
    notifications() {
      return this.getAllNotifications || [];
    },
    unreadCount() {
      return this.getUnreadCount || 0;
    },
    recentNotifications() {
      const all = this.notifications || [];
      // Filter: Only show unread by default, show all when showRead is true
      const filtered = this.showRead ? all : all.filter(n => !n.is_read);
      return filtered.slice(0, 5);
    }
  },
  watch: {
    unreadCount(newCount, oldCount) {
      // If count increased, show a notification
      if (newCount > oldCount && oldCount > 0) {
        this.showNewNotificationAlert();
      }
      this.previousUnreadCount = newCount;
    },
    notifications(newVal) {
      // Notifications array updated
    }
  },
  methods: {
    ...mapActions('notificationModule', [
      'markAsRead',
      'markAllAsRead',
      'fetchNotifications'
    ]),
    getNotificationColor(type) {
      const colors = {
        'NEW_REQUEST': 'info',
        'REQUEST_APPROVED': 'success',
        'REQUEST_DECLINED': 'error'
      };
      return colors[type] || 'secondary';
    },
    getNotificationIcon(type) {
      const icons = {
        'NEW_REQUEST': 'mdi-file-document-plus',
        'REQUEST_APPROVED': 'mdi-check-circle',
        'REQUEST_DECLINED': 'mdi-close-circle'
      };
      return icons[type] || 'mdi-bell';
    },
    async showNewNotificationAlert() {
      try {
        // Check if we're on a platform that supports notifications
        if (!NotificationService) {
          return;
        }

        // Check permission
        const permission = await NotificationService.getPermissionStatus();

        if (permission !== 'granted') {
          return;
        }

        // Get the latest notification details
        const latestNotification = this.notifications && this.notifications.length > 0
          ? this.notifications[0]
          : null;

        if (latestNotification) {
          const title = latestNotification.title || 'New Notification';
          const message = latestNotification.message || 'You have a new notification';

          // Show platform notification (disabled on Android for now)
          await NotificationService.show(title, {
            body: message,
            icon: '/icon-192x192.png',
            data: { timestamp: Date.now(), notificationId: latestNotification.id },
            disableOnAndroid: true
          });
        }
      } catch (error) {
        // Error showing platform notification
      }
    },
    async handleNotificationClick(notification) {
      if (!notification.is_read) {
        await this.markAsRead(notification.id);
      }
      // Navigate to the relevant page based on notification type
      if (notification.type === 'NEW_REQUEST') {
        this.$router.push('/approvals');
      } else if (notification.expense_request_id) {
        this.$router.push(`/requests/${notification.expense_request_id}`);
      }
    },
    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;

      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return 'Just now';
      if (minutes < 60) return `${minutes}m ago`;
      if (hours < 24) return `${hours}h ago`;
      if (days < 7) return `${days}d ago`;

      return date.toLocaleDateString();
    },
    viewAllNotifications() {
      this.$router.push('/notifications');
    },
    async markAllAsRead() {
      const userInfo = this.$store.getters['CurrentUserInfo'];
      if (userInfo && userInfo.id) {
        await this.$store.dispatch('notificationModule/markAllAsRead', userInfo.id);
      }
    },
    async refreshNotifications() {
      const userInfo = this.$store.getters['CurrentUserInfo'];
      this.refreshing = true;
      if (userInfo && userInfo.id) {
        try {
          await this.fetchNotifications(userInfo.id);
          this.offline = false;
        } catch (error) {
          const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
          const networkError = !error?.response || (error?.message || '').toLowerCase().includes('network');
          if (isOffline || networkError) {
            this.offline = true;
          }
        }
      }
      this.refreshing = false;
    }
  },
  async mounted() {
    // Initialize NotificationService and request permissions
    try {
      await NotificationService.init();
      await NotificationService.ensurePermission();
    } catch (error) {
      // NotificationService initialization error
    }

    this.refreshNotifications();

    // Refresh notifications every 30 seconds
    setInterval(() => {
      this.refreshNotifications();
    }, 30000);

    const updateOfflineStatus = () => {
      this.offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    };
    window.addEventListener('online', updateOfflineStatus);
    window.addEventListener('offline', updateOfflineStatus);
    updateOfflineStatus();
  }
};
</script>

<style scoped>
.notification-bell {
  position: relative;
}

.notification-activator {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.offline-pill {
  position: absolute;
  top: -6px;
  right: -2px;
  pointer-events: none;
}

.notification-dropdown {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-border-color));
  box-shadow: 0 8px 24px rgba(var(--v-theme-on-surface-rgb), 0.12);
  color: rgb(var(--v-theme-on-surface));
}

.notification-list {
  overflow-y: auto;
  background: rgb(var(--v-theme-surface));
}

.notification-item {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface-rgb), 0.08);
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
  color: rgb(var(--v-theme-on-surface));
}

.notification-item:hover {
  background-color: rgba(var(--v-theme-on-surface-rgb), 0.04);
}

.notification-item.unread {
  background-color: rgba(var(--v-theme-primary-rgb), 0.12);
}

.notification-title {
  font-weight: 600;
  font-size: 14px;
  line-height: 1.2;
  color: rgb(var(--v-theme-on-surface));
}

.notification-message {
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), 0.78);
  margin-top: 4px;
}

.notification-time {
  font-size: 11px;
  color: rgba(var(--v-theme-on-surface), 0.62);
  margin-top: 2px;
}

.notification-icon {
  color: rgba(var(--v-theme-on-surface), 0.7);
  transition: color 0.2s ease;
}

.notification-icon.has-unread {
  color: rgb(var(--v-theme-primary));
}

.empty-notifications {
  padding: 40px 20px;
  text-align: center;
  color: rgb(var(--v-theme-on-surface-variant));
}

.empty-icon {
  color: rgba(var(--v-theme-on-surface-rgb), 0.25);
}

.empty-message {
  margin-top: 16px;
  margin-bottom: 0;
}
</style>
